/**
 * Hook for triggering and streaming a per-app package update.
 *
 * Calls POST /api/packages/:name/update then connects to
 * SSE at /api/packages/jobs/:jobId/stream
 * for live output. Falls back to polling /status if SSE disconnects.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getCsrfToken } from "../lib/csrf";

export type AppUpdateJobStatus = "idle" | "queued" | "running" | "succeeded" | "failed";

type UseAppUpdateReturn = {
  triggerUpdate: (packageName: string) => Promise<{ ok: boolean; message: string }>;
  isUpdating: boolean;
  jobId: string | null;
  jobStatus: AppUpdateJobStatus;
  jobProgress: number;
  jobStep: string | null;
  jobError: string | null;
  jobLogTail: string[];
  reset: () => void;
};

const POLL_INTERVAL_MS = 3000;

export const useAppUpdate = (): UseAppUpdateReturn => {
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<AppUpdateJobStatus>("idle");
  const [jobProgress, setJobProgress] = useState(0);
  const [jobStep, setJobStep] = useState<string | null>(null);
  const [jobError, setJobError] = useState<string | null>(null);
  const [jobLogTail, setJobLogTail] = useState<string[]>([]);

  const eventSourceRef = useRef<EventSource | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const statusRef = useRef<AppUpdateJobStatus>("idle");
  statusRef.current = jobStatus;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const disconnectSSE = useCallback(() => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    stopPolling();
  }, [stopPolling]);

  /** Polling fallback when SSE disconnects mid-job. */
  const startPollingFallback = useCallback((targetJobId: string) => {
    if (pollTimerRef.current) return;

    const poll = async () => {
      const s = statusRef.current;
      if (s === "succeeded" || s === "failed") {
        stopPolling();
        return;
      }
      try {
        const res = await fetch(`/api/packages/jobs/${targetJobId}/status`, {
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok || !mountedRef.current) return;
        const data = await res.json() as { ok: boolean; data?: { job?: Record<string, unknown> } };
        if (!data.ok || !data.data?.job) return;

        const job = data.data.job;
        const status = job.status as AppUpdateJobStatus;
        setJobStatus(status);

        const lastLines = job.lastLines as string[] | undefined;
        if (lastLines && lastLines.length > 0) {
          setJobLogTail((prev) => {
            const existing = new Set(prev.slice(-lastLines.length));
            const newLines = lastLines.filter((line) => !existing.has(line));
            return newLines.length > 0 ? [...prev, ...newLines] : prev;
          });
        }

        if (status === "succeeded" || status === "failed") {
          stopPolling();
        }
      } catch {
        // Non-fatal, retry next interval
      }
    };

    void poll();
    pollTimerRef.current = setInterval(() => void poll(), POLL_INTERVAL_MS);
  }, [stopPolling]);

  /** Connect to SSE stream for live output. */
  const connectSSE = useCallback((targetJobId: string) => {
    disconnectSSE();

    const es = new EventSource(
      `/api/packages/jobs/${targetJobId}/stream`,
      { withCredentials: true },
    );
    eventSourceRef.current = es;

    es.addEventListener("init", (event: MessageEvent) => {
      if (!mountedRef.current) return;
      try {
        const parsed = JSON.parse(event.data as string) as { status?: string };
        if (parsed.status === "running") {
          setJobStatus("running");
        }
      } catch { /* ignore */ }
    });

    es.addEventListener("line", (event: MessageEvent) => {
      if (!mountedRef.current) return;
      try {
        const parsed = JSON.parse(event.data as string) as { text: string };
        setJobLogTail((prev) => [...prev, parsed.text]);
      } catch { /* ignore */ }
    });

    es.addEventListener("status", (event: MessageEvent) => {
      if (!mountedRef.current) return;
      try {
        const parsed = JSON.parse(event.data as string) as {
          status: string;
          exitCode?: number | null;
          durationMs?: number | null;
        };
        const status = parsed.status as AppUpdateJobStatus;
        if (status === "succeeded" || status === "failed") {
          es.close();
          eventSourceRef.current = null;
          setJobStatus(status);
          if (status === "failed") {
            setJobError(`Update failed${parsed.exitCode != null ? ` (exit code ${parsed.exitCode})` : ""}`);
          }
        } else {
          setJobStatus(status);
        }
      } catch { /* ignore */ }
    });

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      const s = statusRef.current;
      if (s !== "succeeded" && s !== "failed") {
        startPollingFallback(targetJobId);
      }
    };
  }, [disconnectSSE, startPollingFallback]);

  const triggerUpdate = useCallback(
    async (
      packageName: string,
    ): Promise<{ ok: boolean; message: string }> => {
      setJobStatus("queued");
      setJobProgress(0);
      setJobStep(null);
      setJobError(null);
      setJobLogTail([]);

      try {
        const csrfToken = getCsrfToken();
        const headers: Record<string, string> = {};
        if (csrfToken) headers["x-csrf-token"] = csrfToken;

        const res = await fetch(`/api/packages/${encodeURIComponent(packageName)}/update`, {
          method: "POST",
          credentials: "include",
          headers,
        });

        const data = (await res.json()) as {
          ok: boolean;
          data?: { jobId?: string; status?: string; message?: string };
          message?: string;
        };

        if (!res.ok || !data.ok) {
          const msg =
            data.message ?? data.data?.message ?? "Failed to start update";
          setJobStatus("failed");
          setJobError(msg);
          return { ok: false, message: msg };
        }

        const newJobId = data.data?.jobId ?? null;
        setJobId(newJobId);

        if (newJobId) {
          connectSSE(newJobId);
        }

        return {
          ok: true,
          message: data.data?.message ?? "Update started",
        };
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Network error";
        setJobStatus("failed");
        setJobError(msg);
        return { ok: false, message: msg };
      }
    },
    [connectSSE]
  );

  const reset = useCallback(() => {
    disconnectSSE();
    setJobId(null);
    setJobStatus("idle");
    setJobProgress(0);
    setJobStep(null);
    setJobError(null);
    setJobLogTail([]);
  }, [disconnectSSE]);

  const isUpdating = jobStatus === "queued" || jobStatus === "running";

  return {
    triggerUpdate,
    isUpdating,
    jobId,
    jobStatus,
    jobProgress,
    jobStep,
    jobError,
    jobLogTail,
    reset,
  };
};
